import {FormEvent, useEffect, useRef, useState} from "react";
import {Button, Input, Message, Radio, RadioGroup} from "rsuite";
import {ApiError, ContourType, EspFirmwareVersionInfoDto} from "../../../api/artifacts";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import EspTypePicker from "./EspTypePicker";
import "./EspFirmwareCheck.scss";

const getCheckError = (error: unknown) => {
  if (error instanceof ApiError) {
    const body = error.body;
    if (typeof body === "string" && body.trim()) return body;
    if (body && typeof body === "object") {
      const message = body.detail || body.message || body.title;
      if (typeof message === "string") return message;
    }
    if (error.status === 404) return "Прошивка для выбранной модели и контура не найдена.";
    if (error.status === 401) return "Для проверки прошивки необходимо войти в систему.";
    if (error.status === 403) return "Нет прав для проверки прошивки.";
    return `Не удалось проверить прошивку (код ${error.status}).`;
  }
  return "Не удалось связаться с сервером. Повторите проверку.";
};

const EspFirmwareCheck = ({models}: {models: string[]}) => {
  const [expanded, setExpanded] = useState(false);
  const [espType, setEspType] = useState("");
  const [version, setVersion] = useState("");
  const [contour, setContour] = useState<ContourType>("Test");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EspFirmwareVersionInfoDto | null>(null);
  const [error, setError] = useState("");
  const request = useRef<ReturnType<typeof ArtifactsApi.espFirmwares.getCurrent> | null>(null);

  useEffect(() => () => request.current?.cancel(), []);

  const clearResult = () => {
    setResult(null);
    setError("");
  };

  const check = async (event: FormEvent) => {
    event.preventDefault();
    if (loading || !espType.trim() || !version.trim()) return;
    clearResult();
    setLoading(true);
    try {
      request.current = ArtifactsApi.espFirmwares.getCurrent(espType.trim(), version.trim(), contour);
      setResult(await request.current);
    } catch (error) {
      if (!request.current?.isCancelled) setError(getCheckError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="esp-firmware-check">
      <Button appearance="link" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
        Проверить доступную ESP-прошивку
      </Button>
      {expanded && (
        <form onSubmit={check} className="esp-firmware-check__form">
          <EspTypePicker value={espType} models={models} disabled={loading} onChange={(value) => {
            setEspType(value);
            clearResult();
          }} />
          <label className="esp-firmware-check__field">
            <span>Установленная версия</span>
            <Input aria-label="Установленная версия" value={version} disabled={loading} onChange={(value) => {
              setVersion(value);
              clearResult();
            }} />
          </label>
          <RadioGroup inline aria-label="Контур проверки" value={contour} disabled={loading} onChange={(value) => {
            if (value === "Test" || value === "Production") {
              setContour(value);
              clearResult();
            }
          }}>
            <Radio value="Test">Тестовый (Test)</Radio>
            <Radio value="Production">Рабочий (One)</Radio>
          </RadioGroup>
          <Button type="submit" appearance="primary" loading={loading} disabled={loading || !espType.trim() || !version.trim()}>
            Проверить
          </Button>
          {error && <Message type="error" role="alert">{error}</Message>}
          {result && <Message type="info" role="status">
            <div>Контур: {contour === "Test" ? "Test" : "One"}</div>
            <div>Модель: {result.espType || espType.trim()}</div>
            <div>Доступная версия: {result.version || "Не указана"}</div>
            {typeof result.hasNewerFirmwareVersion === "boolean" && <div>
              {result.hasNewerFirmwareVersion ? "Доступно обновление." : "Новой версии нет."}
            </div>}
            {result.description && <div className="esp-firmware-check__description">{result.description}</div>}
          </Message>}
        </form>
      )}
    </section>
  );
};

export default EspFirmwareCheck;
